import { Suspense, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, LoaderIcon } from 'lucide-react';
import { Form } from '@/components/ui/form.tsx';
import {
  Stepper,
  StepperItem,
  StepperTrigger,
  StepperIndicator,
} from '@/components/ui/stepper';
import {
  NotificationRuleFormData,
  createNotificationRuleSchema,
} from '@/features/notification-rules/schemas/create-notification-rule.schema.ts';
import { getCreateNotificationRuleSteps } from '@/features/notification-rules/components/create-notification-rule/create-notification-rule-steps.tsx';
import { cn } from '@/utils/utils.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useScrollToTopHTMLElement } from '@/hooks/use-scroll-to-top-html-element.ts';
import { usePostNotificationRuleMutation } from '@/features/notification-rules/hooks/mutations/use-post-notification-rule.ts';

const INITIAL_STEP = 1;
const DEFAULT_COOLDOWN_PERIOD = 60;
const STEPS = getCreateNotificationRuleSteps();

export const CreateNotificationRuleForm = ({
  onClose,
  initialValues,
  submitLabel = 'Create Rule',
}: {
  onClose: () => void;
  initialValues?: Partial<NotificationRuleFormData>;
  submitLabel?: string;
}) => {
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);

  const { ref, scrollToTop } = useScrollToTopHTMLElement<HTMLDivElement>();

  const form = useForm({
    resolver: zodResolver(createNotificationRuleSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      status: 'enabled',
      notificationTitle: undefined,
      ruleType: 'plant',
      plantId: undefined,
      deviceType: undefined,
      deviceId: undefined,
      conditions: [{ fact: '', operator: 'equal', value: '' }],
      notificationTemplate: undefined,
      notificationPreviewTemplate: undefined,
      cooldownPeriod: DEFAULT_COOLDOWN_PERIOD,
      managedBy: 'self-managed',
      ...initialValues,
    },
  });

  const isLastStep = currentStep === STEPS.length;
  const isFirstStep = currentStep === INITIAL_STEP;

  const resetForm = () => {
    setCurrentStep(INITIAL_STEP);
    form.reset({
      status: 'enabled',
      notificationTitle: undefined,
      ruleType: 'plant',
      plantId: undefined,
      deviceType: undefined,
      deviceId: undefined,
      conditions: [{ fact: '', operator: 'equal', value: '' }],
      notificationTemplate: undefined,
      notificationPreviewTemplate: undefined,
      cooldownPeriod: DEFAULT_COOLDOWN_PERIOD,
      managedBy: 'self-managed',
    });
  };

  const {
    mutate: createNotificationRuleMutation,
    isPending: isCreateNotificationRuleMutationPending,
  } = usePostNotificationRuleMutation({
    onSuccess: () => {
      onClose();
      resetForm();
    },
  });

  const onSubmit = (data: NotificationRuleFormData) => {
    createNotificationRuleMutation({
      ...data,
    });
  };

  const nextStep = async () => {
    const currentStepSchema = STEPS[currentStep - 1].schema;
    const fieldsToValidate = Object.keys(
      currentStepSchema.shape
    ) as (keyof NotificationRuleFormData)[];

    const isValid = await form.trigger(fieldsToValidate);

    if (isValid && !isLastStep) {
      scrollToTop();
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // fields to reset when changing the ruleType
  const watchRuleType = useWatch({ control: form.control, name: 'ruleType' });
  const prevRuleTypeRef = useRef(watchRuleType);
  useEffect(() => {
    if (prevRuleTypeRef.current === watchRuleType) {
      return;
    }
    prevRuleTypeRef.current = watchRuleType;
    form.setValue('deviceType', undefined);
    form.setValue('deviceId', undefined);
    form.setValue('conditions', [{ fact: '', operator: 'equal', value: '' }]);
    form.setValue('notificationTemplate', '');
    form.setValue('notificationPreviewTemplate', '');
  }, [watchRuleType]);

  const CurrentStepElement = STEPS[currentStep - 1].Element;

  return (
    <div ref={ref} className="max-h-[60vh] overflow-auto px-0.5">
      <CreateNotificationRuleFormStepper steps={STEPS} currentStep={currentStep} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Suspense
            fallback={
              <div className="flex h-20 items-center justify-center">
                <LoadingSpinner size={50} />
              </div>
            }
          >
            <CurrentStepElement />
          </Suspense>

          <NavigationButtons
            currentStep={currentStep}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            isCreateNotificationRuleMutationPending={isCreateNotificationRuleMutationPending}
            onPrevious={prevStep}
            onNext={nextStep}
            submitLabel={submitLabel}
          />
        </form>
      </Form>
    </div>
  );
};

function NavigationButtons({
  isFirstStep,
  isLastStep,
  onPrevious,
  isCreateNotificationRuleMutationPending,
  onNext,
  submitLabel = 'Create Rule',
}: {
  currentStep: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isCreateNotificationRuleMutationPending: boolean;
  onPrevious: () => void;
  onNext: () => void;
  submitLabel?: string;
}) {
  return (
    <div className="flex justify-end">
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
          className={cn('flex items-center gap-2', isFirstStep && 'hidden')}
          disabled={isCreateNotificationRuleMutationPending}
        >
          <ArrowLeft size={16} />
          Previous
        </Button>

        <Button
          type="submit"
          className={cn(!isLastStep && 'hidden')}
          disabled={isCreateNotificationRuleMutationPending}
        >
          {isCreateNotificationRuleMutationPending && (
            <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
        <Button
          type="button"
          onClick={onNext}
          className={cn('flex items-center gap-2', isLastStep && 'hidden')}
          disabled={isCreateNotificationRuleMutationPending}
        >
          Next
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function CreateNotificationRuleFormStepper({
  steps,
  currentStep,
}: {
  steps: readonly { step: number }[];
  currentStep: number;
}) {
  return (
    <Stepper value={currentStep} className="items-start gap-4">
      {steps?.map(({ step }) => (
        <StepperItem key={step} step={step} className="flex-1">
          <StepperTrigger asChild className="w-full flex-col items-start gap-2 rounded">
            <div>
              <StepperIndicator asChild className="bg-border h-1 w-full">
                <span className="sr-only">{step}</span>
              </StepperIndicator>
            </div>
          </StepperTrigger>
        </StepperItem>
      ))}
    </Stepper>
  );
}

export default CreateNotificationRuleForm;
